import React, { useState, useRef, useEffect } from "react";
import { useAskRankings } from "../apiCalls/aiApiCalls";
import { Container, Card, Badge, Spinner } from "../components/ui";
import {
  SparkleIcon,
  UsersIcon,
  TrophyIcon,
  TrendIcon,
  BuildingIcon,
  ArrowRightIcon,
} from "../components/ui/icons";

const EXAMPLE_PROMPTS = [
  "Who is the top ranked player and how many points do they have?",
  "Compare the top two players.",
  "Who are the highest ranked female players?",
  "How many active players are there in total?",
  "Which countries have the most ranked players?",
];

// The static "what this can do" rail. These describe the assistant's real
// scope — it answers from the rankings data the API already exposes.
const CAPABILITIES = [
  {
    icon: <UsersIcon />,
    title: "Player Rankings & Profiles",
    body: "Get rankings, points, and player comparisons.",
  },
  {
    icon: <TrophyIcon />,
    title: "Tournaments",
    body: "Find events on the global calendar.",
  },
  {
    icon: <TrendIcon />,
    title: "Ranking System",
    body: "Learn how PadelRank calculates rankings.",
  },
  {
    icon: <BuildingIcon />,
    title: "Clubs & Countries",
    body: "Explore clubs and rankings by country.",
  },
];

const AskRankings = () => {
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState([]); // { role: "user" | "assistant", text }
  const threadEndRef = useRef(null);

  const { mutateAsync, isLoading } = useAskRankings();

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread, isLoading]);

  const submitQuestion = async (q) => {
    const trimmed = q.trim();
    if (!trimmed || isLoading) return;

    setThread((prev) => [...prev, { role: "user", text: trimmed }]);
    setQuestion("");

    try {
      const res = await mutateAsync(trimmed);
      setThread((prev) => [
        ...prev,
        { role: "assistant", text: res?.data?.answer ?? "" },
      ]);
    } catch (err) {
      // Toast is shown by the hook; surface a fallback line in the thread too.
      setThread((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Sorry, I couldn't answer that right now. Please try again.",
        },
      ]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitQuestion(question);
  };

  return (
    <Container className="py-8 md:py-10">
      <div className="grid gap-6 lg:grid-cols-[16rem_1fr] xl:grid-cols-[16rem_1fr_20rem]">
        {/* ----------------------------- Left rail ----------------------------- */}
        <aside className="space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <SparkleIcon className="text-primary" />
              <h1 className="text-lg font-bold text-navy">PadelRank AI</h1>
              <Badge tone="primary">BETA</Badge>
            </div>
            <p className="mt-2 text-sm text-muted">
              Your intelligent assistant for all things padel.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setThread([])}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-sm bg-primary text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
          >
            + New Chat
          </button>

          <Card padded={false}>
            <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-navy">
              Suggested Questions
            </h2>
            <ul className="divide-y divide-border">
              {EXAMPLE_PROMPTS.map((prompt) => (
                <li key={prompt}>
                  <button
                    type="button"
                    onClick={() => submitQuestion(prompt)}
                    disabled={isLoading}
                    className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm text-muted transition-colors hover:bg-bg hover:text-navy disabled:opacity-50"
                  >
                    {prompt}
                    <ArrowRightIcon className="h-4 w-4 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </aside>

        {/* ------------------------------- Chat -------------------------------- */}
        <Card padded={false} className="flex min-h-[34rem] flex-col">
          <div className="flex-1 overflow-y-auto p-5 md:p-8">
            {thread.length === 0 && !isLoading ? (
              <div className="max-w-lg">
                <h2 className="text-2xl font-bold leading-snug tracking-tight text-navy md:text-3xl">
                  Ask anything about padel rankings, players, tournaments and
                  more.
                </h2>
                <p className="mt-3 text-muted">
                  PadelRank AI answers from the live rankings in our global
                  database.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {thread.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${
                      msg.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {msg.role === "assistant" && (
                      <SparkleIcon className="mr-2 mt-1 h-5 w-5 shrink-0 text-primary" />
                    )}
                    <div
                      className={`max-w-[85%] whitespace-pre-wrap rounded-md px-4 py-2.5 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-primary-wash text-navy"
                          : "bg-bg text-navy"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <Spinner size="sm" />
                    Thinking…
                  </div>
                )}
                <div ref={threadEndRef} />
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="border-t border-border p-4 md:p-5"
          >
            <div className="flex gap-2">
              <input
                className="h-11 flex-1 rounded-sm border border-border-strong bg-surface px-3 text-sm text-navy placeholder:text-muted focus-ring"
                type="text"
                placeholder="Ask anything about padel…"
                value={question}
                maxLength={500}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !question.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-primary text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Send question"
              >
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-muted">
              PadelRank AI can make mistakes. Please verify important
              information.
            </p>
          </form>
        </Card>

        {/* ---------------------------- Right rail ----------------------------- */}
        <aside className="hidden xl:block">
          <Card>
            <h2 className="mb-4 font-semibold text-navy">
              What can I help you with?
            </h2>
            <ul className="space-y-4">
              {CAPABILITIES.map((item) => (
                <li key={item.title} className="flex gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-primary-wash text-primary">
                    {item.icon}
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-navy">
                      {item.title}
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted">
                      {item.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </aside>
      </div>
    </Container>
  );
};

export default AskRankings;
